import axios from 'axios';
import type { CreateContactDto } from './leadsService';
import type { ClientCurrentStageDto } from './clientsService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7271/api';
const BASE = `${API_BASE_URL}/agent-portal`;

// ----- Enums (matching backend AgentPortalDto.cs) -----

export enum AgentLeadStage {
  New = 1,
  InProgress = 2,
  NotRelevant = 3,
}

export enum AgentClientStage {
  InProgress = 1,
  AwaitingCommittee = 2,
  Approved = 3,
  Rejected = 4,
  Closed = 5,
}

/** CommitteeDecisionStatus בשרת */
export enum CommitteeDecision {
  Pending = 1,
  ApprovedPermanent = 2,
  Rejected = 3,
  ApprovedForPeriod = 4,
}

/** LeadSourceType בשרת */
export enum AgentSourceType {
  Agent = 1,
  Organization = 2,
}

export const LEAD_STAGE_OPTIONS: { value: AgentLeadStage; label: string }[] = [
  { value: AgentLeadStage.New, label: 'חדש' },
  { value: AgentLeadStage.InProgress, label: 'בטיפול' },
  { value: AgentLeadStage.NotRelevant, label: 'לא רלוונטי' },
];

export const CLIENT_STAGE_OPTIONS: { value: AgentClientStage; label: string }[] = [
  { value: AgentClientStage.InProgress, label: 'בטיפול' },
  { value: AgentClientStage.AwaitingCommittee, label: 'ממתין לוועדה' },
  { value: AgentClientStage.Approved, label: 'אושר' },
  { value: AgentClientStage.Rejected, label: 'נדחה' },
  { value: AgentClientStage.Closed, label: 'התיק נסגר' },
];

export const getLeadStageLabel = (stage: AgentLeadStage): string =>
  LEAD_STAGE_OPTIONS.find(o => o.value === stage)?.label ?? '';

export const getClientStageLabel = (stage: AgentClientStage): string =>
  CLIENT_STAGE_OPTIONS.find(o => o.value === stage)?.label ?? '';

export const getCommitteeDecisionLabel = (decision: CommitteeDecision): string => {
  switch (decision) {
    case CommitteeDecision.ApprovedPermanent:
      return 'אושר לצמיתות';
    case CommitteeDecision.ApprovedForPeriod:
      return 'אושר לתקופה';
    case CommitteeDecision.Rejected:
      return 'נדחה';
    default:
      return 'ממתין להחלטה';
  }
};

// ----- Types -----

export interface AgentPaged<T> {
  items: T[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

export interface AgentPortalMe {
  sourceId: number;
  name: string;
  type: AgentSourceType;
}

export interface AgentLeadRow {
  id: number;
  fullName: string;
  benefitType: number;
  otherBenefitDescription?: string | null;
  stage: AgentLeadStage;
  statusName: string;
  statusColor: string;
  referredAt: string;
  lastUpdatedAt: string;
}

export interface AgentCommittee {
  committeeDate: string;
  evaluationNumber: number;
  decisionStatus: CommitteeDecision;
  disabilityPercentage?: number | null;
  approvalEndDate?: string | null;
}

export interface AgentClientRow {
  id: number;
  fullName: string;
  benefitType?: number | null;
  signedAt: string;
  stage: AgentClientStage;
  statusName?: string; // הסטטוס הנוכחי של הלקוח — חסר כשלא הוגדר
  statusColor?: string;
  currentStages: ClientCurrentStageDto[]; // כל הסטטוסים הפעילים במקביל
  upcomingCommittee?: AgentCommittee | null;
  lastDecision?: AgentCommittee | null;
}

export interface AgentClientDetail extends AgentClientRow {
  committees: AgentCommittee[];
}

export interface AgentUpcomingCommittee {
  clientId: number;
  clientName: string;
  committeeDate: string;
}

export interface AgentRecentReferral {
  id: number;
  isClient: boolean;
  fullName: string;
  leadStage?: AgentLeadStage | null;
  clientStage?: AgentClientStage | null;
  statusName?: string;
  statusColor?: string;
  currentStages?: ClientCurrentStageDto[]; // ללקוח בלבד
  updatedAt: string;
}

export interface AgentPortalSummary {
  sourceName: string;
  sourceType: AgentSourceType;
  activeLeadsCount: number;
  signedClientsCount: number;
  approvedClientsCount: number;
  totalOwed: number;
  totalPaid: number;
  remaining: number;
  upcomingCommittees: AgentUpcomingCommittee[];
  recentReferrals: AgentRecentReferral[];
}

export interface AgentPaymentRow {
  paymentDate: string;
  amount: number;
  reference?: string | null;
}

export interface AgentCommissionClientRow {
  clientId: number;
  clientName: string;
  signingOwed: number;
  approvalOwed?: number | null; // null = טרם חושבה
  totalOwed: number;
  totalPaid: number;
  remaining: number;
  payments: AgentPaymentRow[];
}

export interface AgentCommissions {
  totalOwed: number;
  totalPaid: number;
  remaining: number;
  pendingLeadCount: number;
  items: AgentCommissionClientRow[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

export interface AgentListFilter {
  page: number;
  pageSize: number;
  search?: string;
  stage?: number;
}

/** הפניית ליד חדש — המקור והעמלות נקבעים בשרת לפי הרשומה המקושרת. */
export interface AgentCreateLead {
  fullName: string;
  benefitType: number;
  otherBenefitDescription?: string;
  clientIdNumber: string;
  isClientForeignResident: boolean;
  clientBirthDate?: string | null; // YYYY-MM-DD
  clientAddress?: string;
  clientCity?: string;
  contacts: CreateContactDto[];
}

export interface AgentLeadCreated {
  id: number;
  fullName: string;
  uploadedDocumentsCount: number;
  failedDocuments: string[];
}

// ----- API -----

export const agentPortalApi = {
  getMe: async (): Promise<AgentPortalMe> => {
    const response = await axios.get<AgentPortalMe>(`${BASE}/me`);
    return response.data;
  },

  getSummary: async (signal?: AbortSignal): Promise<AgentPortalSummary> => {
    const response = await axios.get<AgentPortalSummary>(`${BASE}/summary`, { signal });
    return response.data;
  },

  getLeads: async (filter: AgentListFilter, signal?: AbortSignal): Promise<AgentPaged<AgentLeadRow>> => {
    const response = await axios.get<AgentPaged<AgentLeadRow>>(`${BASE}/leads`, { params: filter, signal });
    return response.data;
  },

  getClients: async (filter: AgentListFilter, signal?: AbortSignal): Promise<AgentPaged<AgentClientRow>> => {
    const response = await axios.get<AgentPaged<AgentClientRow>>(`${BASE}/clients`, { params: filter, signal });
    return response.data;
  },

  getClientDetail: async (clientId: number, signal?: AbortSignal): Promise<AgentClientDetail> => {
    const response = await axios.get<AgentClientDetail>(`${BASE}/clients/${clientId}`, { signal });
    return response.data;
  },

  getCommissions: async (page: number, pageSize: number, signal?: AbortSignal): Promise<AgentCommissions> => {
    const response = await axios.get<AgentCommissions>(`${BASE}/commissions`, { params: { page, pageSize }, signal });
    return response.data;
  },

  /** ליד + מסמכים בבקשה אחת (multipart): data = JSON של הליד, files = המסמכים. */
  createLead: async (lead: AgentCreateLead, files: File[]): Promise<AgentLeadCreated> => {
    const formData = new FormData();
    formData.append('data', JSON.stringify(lead));
    files.forEach(file => formData.append('files', file));
    const response = await axios.post<AgentLeadCreated>(`${BASE}/leads`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

// ----- Formatting helpers -----

export const formatDateTime = (dateString?: string | null): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const datePart = date.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const timePart = date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
  return `${datePart}, ${timePart}`;
};
